import {
  Monitor, Printer, Package, Wrench, Network, Cctv, Store, Building2,
  Zap, Usb, ClipboardCheck, Settings, ShieldCheck, Wifi, HardDrive, Cable,
  Router, Laptop, Download, RefreshCw, Bug, Cpu, CircuitBoard, Database,
  MemoryStick, Fan, Wind, Box, Keyboard, Power, FileText, Sparkles,
} from "lucide-react";
import { IMAGES, WA_MESSAGES } from "@/data/site";

export const INDEX_CONFIGS = {
  "/products": {
    path: "/products",
    metaTitle: "Technology Products for Homes, Businesses & Institutions | CTS",
    metaDesc:
      "Computers, assembled PCs, printers, toners, components, networking, CCTV and UPS products from Cherians Tech Solutions, Mallappally.",
    eyebrow: "PRODUCTS",
    breadcrumbLabel: "Products",
    h1: "Technology Products for Homes, Businesses & Institutions",
    intro:
      "We supply a range of computers, assembled PCs, printers, toners, networking equipment, CCTV products, components and accessories.",
    cols: 4,
    cards: [
      { title: "Computers & Assembled PCs", text: "Custom-built desktop PCs configured according to your requirements and budget.", to: "/products/assembled-computers", img: IMAGES.workspace, imgKey: "workspace", alt: "Assembled desktop computer on an office desk" },
      { title: "Printers", text: "Printers for home, office and business use, with installation and configuration.", to: "/products/printers", img: IMAGES.printer, imgKey: "printer", alt: "Office printer" },
      { title: "Toners & Consumables", text: "Printer toners and consumables for your everyday printing requirements.", to: "/products/toners", img: IMAGES.toner, imgKey: "toner", alt: "Printer toner cartridge" },
      { title: "Computer Components & Accessories", text: "Processors, motherboards, RAM, storage, graphics cards, cabinets and accessories.", to: "/products/components", img: IMAGES.circuit, imgKey: "circuit", alt: "Computer components and circuit board" },
      { title: "Networking Solutions", text: "Routers, switches, Wi-Fi equipment, accessories and cabling.", to: "/products/networking", img: IMAGES.network, imgKey: "network", alt: "Network equipment and cabling" },
      { title: "CCTV & Surveillance", text: "CCTV cameras, recorders and related products with installation.", to: "/products/cctv", img: IMAGES.cctv, imgKey: "cctv", alt: "CCTV security camera" },
      { title: "UPS & Power", text: "Power protection and backup for computers, offices and equipment.", to: "/products/ups", img: IMAGES.ups, imgKey: "ups", alt: "UPS power backup unit" },
    ],
    cta: { requirement: undefined, waMsg: WA_MESSAGES.general },
  },
  "/services": {
    path: "/services",
    metaTitle: "Computer & IT Services in Mallappally | Cherians Tech Solutions",
    metaDesc:
      "Computer and laptop repair, installation, networking and CCTV services in Mallappally and surrounding areas from CTS.",
    eyebrow: "SERVICES",
    h1: "Services",
    intro:
      "Technology problems can interrupt your work. CTS provides local technical services for computers, laptops, networking and CCTV — with on-site support within approximately 15–20 km of Mallappally.",
    cols: 3,
    cards: [
      { title: "Computer & Laptop Service", text: "Diagnostics, troubleshooting, upgrades and repair for desktop computers and laptops.", to: "/services/computer-service", img: IMAGES.heroTech, imgKey: "heroTech", alt: "Computer and laptop being serviced" },
      { title: "Installation & Configuration", text: "Computer, printer, network and device installation and configuration.", to: "/services/installation", img: IMAGES.workspace, imgKey: "workspace", alt: "Computer installation at a workspace" },
      { title: "Networking Services", text: "Network installation, configuration and technical support.", to: "/services/networking", img: IMAGES.network, imgKey: "network", alt: "Network cabling installation" },
      { title: "CCTV Installation", text: "CCTV planning, supply and installation for homes and businesses.", to: "/services/cctv-installation", img: IMAGES.cctv, imgKey: "cctv", alt: "CCTV camera installation" },
      { title: "Solar Energy Solutions", text: "CTS is preparing to expand into solar energy solutions for homes and businesses.", to: "/services/solar", img: IMAGES.solar, imgKey: "solar", alt: "Solar panels", badge: "COMING SOON" },
    ],
    cta: { requirement: "Computer & Laptop Service", waMsg: WA_MESSAGES.service },
  },
  "/business-solutions": {
    path: "/business-solutions",
    metaTitle: "Business Solutions | CTS Mallappally, Kerala",
    metaDesc:
      "CTS helps businesses, offices, hospitals, banks and institutions with computers, printers, networking, CCTV, installation and technical support.",
    eyebrow: "BUSINESS SOLUTIONS",
    h1: "Business Solutions",
    intro:
      "From computers and printers to networking, CCTV, installation and technical support, CTS helps businesses and institutions choose, install and maintain the technology they depend on.",
    cols: 3,
    cards: [
      { title: "Small Business", text: "Practical technology for shops, agencies and growing businesses.", to: "/business-solutions/small-business", img: IMAGES.smallbiz, imgKey: "smallbiz", alt: "Small business team at work" },
      { title: "Offices", text: "Computers, printers, networking and support for productive offices.", to: "/business-solutions/offices", img: IMAGES.office, imgKey: "office", alt: "Modern office workspace" },
      { title: "Hospitals & Clinics", text: "Dependable workstations, printing, networking and CCTV.", to: "/business-solutions/hospitals", img: IMAGES.hospital, imgKey: "hospital", alt: "Hospital corridor" },
      { title: "Banks & Financial Offices", text: "Technology products, installation and technical support for private-sector banks and financial offices.", to: "/business-solutions/banks", img: IMAGES.bank, imgKey: "bank", alt: "Financial office building" },
      { title: "Institutions", text: "Technology supply, installation and support for institutions.", quoteReq: "Institution / Institutional Requirement", img: IMAGES.institution, imgKey: "institution", alt: "Institutional campus building", ctaLabel: "GET A QUOTE" },
    ],
    cta: { title: "Discuss Your Requirement", text: "Tell us what your organization needs — we'll recommend a practical solution.", requirement: "Business IT Requirements", waMsg: WA_MESSAGES.business, primaryLabel: "DISCUSS YOUR REQUIREMENT" },
  },
};

export const PAGE_CONFIGS = {
  "/products/assembled-computers": {
    path: "/products/assembled-computers",
    parent: { label: "Products", to: "/products" },
    metaTitle: "Computers & Assembled PCs in Mallappally | CTS",
    metaDesc:
      "Assembled PCs built around your requirements and budget — home, office and business computers from Cherians Tech Solutions, Mallappally.",
    eyebrow: "ASSEMBLED COMPUTERS",
    h1: "Computers & Assembled PCs",
    intro:
      "A computer should match the work you need it to do. We assemble PCs based on your requirements, applications and budget.",
    trackEvent: "product_view",
    heroImgKey: "workspace",
    blocks: [
      {
        type: "cards",
        heading: "A PC for Every Requirement",
        items: [
          { icon: Monitor, title: "Home Computers", text: "Dependable PCs for everyday use — browsing, studies, media and home work." },
          { icon: Building2, title: "Office Computers", text: "Reliable systems for documents, email, accounting and daily office work." },
          { icon: Store, title: "Business PCs", text: "Computers configured for billing, business applications and multi-user work." },
        ],
      },
      { type: "steps", heading: "How It Works", steps: ["Tell us your requirements", "We recommend suitable components", "We recommend the configuration", "We assemble and test", "We install and configure"] },
      {
        type: "consider",
        eyebrow: "WHY ASSEMBLED",
        heading: "Why Choose an Assembled PC?",
        items: ["Built Around Your Requirement", "Choose Where to Spend", "Easier to Upgrade", "Tested Before Delivery", "Local Technical Support"],
      },
    ],
    form: {
      title: "Request a PC Configuration",
      sub: "Tell us what you'll use the computer for and we'll recommend a configuration.",
      formType: "pc-config",
      requirement: "Assembled PC",
      fields: ["name", "phone", "location", "message"],
      customFields: [
        { name: "usage", label: "Primary Use", type: "select", options: ["Home", "Office", "Business"] },
        { name: "preferred_setup", label: "Preferred Setup", type: "select", options: ["Desktop only", "Desktop + Monitor", "Complete setup", "Not sure"] },
        { name: "budget", label: "Approximate Budget (optional)", type: "text" },
      ],
      submitLabel: "REQUEST A PC CONFIGURATION",
      trackEvent: "quote_submitted",
    },
    cta: { requirement: "Assembled PC", waMsg: WA_MESSAGES.computer, primaryLabel: "BUILD MY PC" },
  },
  "/products/printers": {
    path: "/products/printers",
    parent: { label: "Products", to: "/products" },
    metaTitle: "Printers in Mallappally | Cherians Tech Solutions",
    metaDesc:
      "Printer sales, installation and recommendation in Mallappally. CTS helps you choose the right printer based on volume, running cost and usage.",
    eyebrow: "PRINTERS",
    h1: "Printers",
    intro:
      "Choosing the right printer isn't just about the purchase price. Running cost, printing volume, speed and maintenance all matter.",
    trackEvent: "product_view",
    heroImgKey: "printer",
    blocks: [
      {
        type: "cards",
        heading: "Printer Categories",
        cols: 4,
        items: [
          { icon: FileText, title: "Monochrome Laser Printers", text: "Cost-effective black-and-white printing for everyday documents." },
          { icon: Package, title: "Multifunction Laser Printers", text: "Print, scan and copy in a single laser device, where applicable." },
          { icon: Sparkles, title: "Colour Ink-Tank Printers", text: "Economical colour printing with refillable ink tanks." },
          { icon: Printer, title: "Multifunction Ink-Tank Printers", text: "Print, scan and copy with cost-effective ink-tank technology, where applicable." },
        ],
      },
      {
        type: "consider",
        heading: "Choose the Right Printer for Your Needs",
        items: ["Printing volume", "Colour vs monochrome", "Running cost", "Connectivity", "Scan / copy requirements", "Business use"],
      },
      {
        type: "split",
        eyebrow: "BEYOND THE PURCHASE",
        title: "Your Printer Doesn't End With the Purchase",
        paragraphs: ["We can help with printer selection, installation and toner requirements — so your printer keeps working the way it should."],
        image: IMAGES.printer,
        imgKey: "printer",
        alt: "Printer supplied and installed by CTS",
      },
      { type: "steps", heading: "How We Help", steps: ["Choose", "Install", "Keep Printing"] },
    ],
    form: {
      title: "Get a Printer Recommendation",
      sub: "Tell us how you plan to use the printer and we'll suggest suitable options.",
      formType: "printer",
      requirement: "Printer",
      fields: ["name", "phone", "message"],
      customFields: [
        { name: "customer_type", label: "Customer Type", type: "select", options: ["Home", "Office", "Business"] },
        { name: "monthly_volume", label: "Approx. Monthly Print Volume (optional)", type: "select", options: ["Low (occasional)", "Medium (regular office use)", "High (business volume)"] },
      ],
      submitLabel: "GET A PRINTER RECOMMENDATION",
      trackEvent: "quote_submitted",
    },
    cta: { requirement: "Printer", waMsg: WA_MESSAGES.printer, primaryLabel: "ASK FOR PRICE" },
  },
  "/products/toners": {
    path: "/products/toners",
    parent: { label: "Products", to: "/products" },
    metaTitle: "Toners & Consumables in Mallappally | CTS",
    metaDesc:
      "Printer toners and consumables in Mallappally and Pathanamthitta. Send your printer brand and model to CTS and check toner or ink availability.",
    eyebrow: "TONERS & CONSUMABLES",
    h1: "Toners & Consumables",
    intro:
      "Need a toner or ink for your printer? CTS supplies printer toners, ink and related consumables for customers in and around Mallappally and Pathanamthitta.",
    trackEvent: "product_view",
    heroImgKey: "toner",
    blocks: [
      {
        type: "split",
        eyebrow: "KEEP PRINTING",
        title: "Consumables for Laser and Ink-Tank Printers",
        paragraphs: [
          "Share your printer brand and model, and we'll check availability of toner or ink for you. We supply consumables for both laser and ink-tank printers used by home and business customers.",
        ],
        points: ["Laser toners & cartridges", "Ink-tank ink, including colour ink", "Availability check before you visit", "Local pickup from Mallappally"],
        image: IMAGES.toner,
        imgKey: "toner",
        alt: "Printer toner cartridge",
      },
    ],
    form: {
      title: "Check Toner or Ink Availability",
      sub: "Tell us your printer details and we'll confirm availability.",
      formType: "toner",
      requirement: "Toner / Consumables",
      fields: ["name", "phone"],
      customFields: [
        { name: "printer_brand", label: "Printer Brand", type: "text", required: true },
        { name: "printer_model", label: "Printer Model", type: "text", required: true },
        { name: "consumable_type", label: "Consumable Type", type: "select", options: ["Toner", "Ink", "Not sure"] },
        { name: "toner_number", label: "Toner / Cartridge / Ink Number (optional)", type: "text" },
        { name: "quantity", label: "Quantity", type: "number", required: true },
      ],
      submitLabel: "CHECK AVAILABILITY",
      trackEvent: "toner_enquiry_submitted",
    },
    cta: { requirement: "Toner / Consumables", waMsg: WA_MESSAGES.toner, primaryLabel: "CHECK AVAILABILITY" },
  },
  "/products/components": {
    path: "/products/components",
    parent: { label: "Products", to: "/products" },
    metaTitle: "Computer Components & Accessories in Mallappally | CTS",
    metaDesc:
      "Processors, motherboards, RAM, SSD storage, graphics cards, SMPS, cooling fans, PC cabinets and accessories from CTS Mallappally. Enquire for availability and price.",
    eyebrow: "COMPONENTS & ACCESSORIES",
    h1: "Computer Components & Accessories",
    intro:
      "Building, upgrading or repairing a computer? CTS supplies computer components and accessories — tell us what you need and we'll confirm availability and price.",
    trackEvent: "product_view",
    heroImage: IMAGES.circuit,
    heroImgKey: "circuit",
    heroAlt: "Computer components and circuit board",
    blocks: [
      {
        type: "cards",
        heading: "Core Components",
        cols: 4,
        items: [
          { icon: Cpu, title: "Processors", text: "CPUs for new builds and upgrades." },
          { icon: CircuitBoard, title: "Motherboards", text: "Boards matched to your processor and requirement." },
          { icon: MemoryStick, title: "RAM", text: "Memory for new systems and upgrades." },
          { icon: HardDrive, title: "SSD / Storage", text: "SSDs and storage drives." },
        ],
      },
      {
        type: "cards",
        heading: "Power & Cooling",
        cols: 3,
        items: [
          { icon: Power, title: "SMPS / Power Supplies", text: "Power supplies for reliable operation." },
          { icon: Fan, title: "CPU Coolers", text: "Cooling for your processor." },
          { icon: Wind, title: "Cabinet Cooling Fans", text: "Airflow for your cabinet." },
        ],
      },
      {
        type: "cards",
        heading: "PC Build & Accessories",
        cols: 2,
        items: [
          { icon: Box, title: "PC Cabinets", text: "Cabinets for new assembled PCs." },
          { icon: Keyboard, title: "Computer Accessories", text: "Keyboards, mice and everyday accessories." },
        ],
      },
      {
        type: "text",
        heading: "Specialised: Graphics Cards",
        paragraphs: ["Graphics cards are available for specific graphics-intensive or performance requirements — tell us about your requirement and we'll confirm availability."],
      },
    ],
    cta: { title: "Looking for a Component?", text: "Tell us what you need — CTS will confirm availability and price.", requirement: "Computer Components & Accessories", waMsg: WA_MESSAGES.general, primaryLabel: "ENQUIRE ABOUT COMPONENTS" },
  },
  "/products/networking": {
    path: "/products/networking",
    parent: { label: "Products", to: "/products" },
    metaTitle: "Networking Solutions in Mallappally | CTS",
    metaDesc:
      "Routers, switches, Wi-Fi, network accessories and cabling in Mallappally. CTS helps you select, install and configure the right networking solution.",
    eyebrow: "NETWORKING",
    h1: "Networking Solutions",
    intro:
      "From individual network equipment to complete office networking requirements, CTS can help you select, install and configure the right solution.",
    trackEvent: "product_view",
    heroImgKey: "network",
    blocks: [
      {
        type: "cards",
        heading: "Networking Products",
        items: [
          { icon: Router, title: "Routers", text: "Routers for home and business connectivity requirements." },
          { icon: Network, title: "Switches", text: "Network switches for wired office and institutional networks." },
          { icon: Wifi, title: "Wi-Fi / Access Points", text: "Wi-Fi equipment for better wireless coverage and reliability." },
          { icon: Usb, title: "Network Accessories", text: "Accessories and components for your network setup." },
          { icon: Cable, title: "Cabling", text: "Network cabling for structured, dependable connections." },
        ],
      },
      {
        type: "text",
        heading: "Need Installation or Configuration?",
        paragraphs: ["We can also install and configure the networking equipment you buy from us — just mention it when you send your enquiry below."],
      },
      {
        type: "split",
        eyebrow: "FOR BUSINESS",
        title: "Better Connectivity for Your Business",
        paragraphs: ["From network equipment to installation and configuration, CTS helps businesses build reliable connectivity."],
        image: IMAGES.network,
        imgKey: "network",
        alt: "Business networking installation",
      },
    ],
    cta: { title: "Need Help With Your Networking Requirement?", text: "Tell us about your premises and what you need to connect — we'll recommend a practical solution.", requirement: "Networking", waMsg: WA_MESSAGES.networking, primaryLabel: "DISCUSS YOUR NETWORKING REQUIREMENT" },
  },
  "/products/cctv": {
    path: "/products/cctv",
    parent: { label: "Products", to: "/products" },
    metaTitle: "CCTV & Surveillance in Mallappally | CTS",
    metaDesc:
      "CCTV cameras, DVR/NVR recorders, storage and accessories for homes, businesses and institutions in Mallappally, from Cherians Tech Solutions.",
    eyebrow: "CCTV & SURVEILLANCE",
    h1: "CCTV & Surveillance",
    intro:
      "Improve visibility with a properly planned CCTV system for your home, business or institution.",
    trackEvent: "product_view",
    heroImgKey: "cctv",
    blocks: [
      {
        type: "cards",
        heading: "CCTV Equipment We Supply",
        cols: 4,
        items: [
          { icon: Cctv, title: "CCTV Cameras", text: "Cameras suited to different property and coverage requirements." },
          { icon: HardDrive, title: "DVR / NVR Recorders", text: "Recording equipment for storing and managing CCTV footage." },
          { icon: Database, title: "CCTV Storage", text: "Storage solutions for CCTV recording requirements, where applicable." },
          { icon: Cable, title: "CCTV Accessories", text: "Supporting equipment and accessories for CCTV installations." },
        ],
      },
      {
        type: "note",
        text: "Need CCTV installation or configuration? CTS can help with planning, installation and setup.",
        link: { label: "VIEW CCTV INSTALLATION SERVICES", to: "/services/cctv-installation" },
      },
    ],
    cta: { requirement: "CCTV", waMsg: WA_MESSAGES.cctv, primaryLabel: "REQUEST A CCTV QUOTE" },
  },
  "/products/ups": {
    path: "/products/ups",
    parent: { label: "Products", to: "/products" },
    metaTitle: "UPS & Power in Mallappally | CTS",
    metaDesc:
      "Standard UPS and power backup solutions for computers, offices and small businesses from Cherians Tech Solutions, Mallappally. Enquire for availability.",
    eyebrow: "UPS & POWER",
    h1: "UPS & Power",
    intro:
      "Standard UPS solutions for homes, offices and small businesses — protecting computers and essential equipment from outages and fluctuations.",
    trackEvent: "product_view",
    heroImage: IMAGES.ups,
    heroImgKey: "ups",
    heroAlt: "UPS power backup unit",
    blocks: [
      {
        type: "consider",
        eyebrow: "WHY POWER BACKUP",
        heading: "Protect Your Equipment and Work",
        items: ["Backup power for computers and workstations", "Protection against outages and fluctuations", "Power backup for essential office equipment", "Help choosing the right capacity for your load"],
      },
    ],
    cta: { title: "Need a UPS or Power Backup?", text: "Tell us what equipment you need to protect — CTS will confirm availability and price.", requirement: "Other", waMsg: WA_MESSAGES.ups, primaryLabel: "ENQUIRE ABOUT UPS" },
  },
  "/services/computer-service": {
    path: "/services/computer-service",
    parent: { label: "Services", to: "/services" },
    metaTitle: "Computer & Laptop Service in Mallappally | CTS",
    metaDesc:
      "Computer and laptop repair in Mallappally — diagnostics, hardware troubleshooting, upgrades, software installation and performance issues. Book a service with CTS.",
    eyebrow: "COMPUTER & LAPTOP SERVICE",
    h1: "Computer & Laptop Service",
    intro:
      "Having trouble with your computer or laptop? CTS provides local technical service and troubleshooting for common hardware and software-related problems.",
    trackEvent: "service_view",
    heroImgKey: "heroTech",
    blocks: [
      {
        type: "cards",
        heading: "Service Areas",
        cols: 4,
        items: [
          { icon: ClipboardCheck, title: "Diagnostics", text: "Identify what's actually wrong before anything is replaced." },
          { icon: Wrench, title: "Hardware Troubleshooting", text: "Troubleshooting for common hardware-related problems." },
          { icon: Zap, title: "Upgrades", text: "Memory, storage and component upgrades to extend usable life." },
          { icon: Download, title: "Software Installation", text: "Installation and setup of the software you need." },
          { icon: Laptop, title: "Operating System Issues", text: "Help with operating system-related problems and setup." },
          { icon: Bug, title: "Performance Issues", text: "Slow systems diagnosed and improved where possible." },
          { icon: RefreshCw, title: "Component Replacement", text: "Replacement of faulty components where required." },
        ],
      },
    ],
    form: {
      title: "Book a Service",
      sub: "Describe the problem and we'll get back to you.",
      formType: "service",
      requirement: "Computer & Laptop Service",
      fields: ["name", "phone", "location", "message"],
      submitLabel: "BOOK A SERVICE",
      trackEvent: "service_request_submitted",
    },
    cta: { requirement: "Computer & Laptop Service", waMsg: WA_MESSAGES.service, primaryLabel: "BOOK A SERVICE" },
  },
  "/services/installation": {
    path: "/services/installation",
    parent: { label: "Services", to: "/services" },
    metaTitle: "Installation & Configuration Services | CTS Mallappally",
    metaDesc:
      "Computer setup, printer installation, network setup, CCTV installation and device configuration in Mallappally and surrounding areas.",
    eyebrow: "INSTALLATION",
    h1: "Installation & Configuration",
    intro:
      "Buying technology is only the first step. Proper installation and configuration ensure that your equipment works as intended.",
    trackEvent: "service_view",
    heroImgKey: "workspace",
    blocks: [
      {
        type: "cards",
        heading: "Installation Services",
        items: [
          { icon: Monitor, title: "Computer Setup", text: "New computer setup, configuration and preparation for use.", quoteReq: "Installation / Configuration", cta: "REQUEST INSTALLATION" },
          { icon: Printer, title: "Printer Installation", text: "Printer installation, driver setup and configuration.", quoteReq: "Installation / Configuration", cta: "REQUEST INSTALLATION" },
          { icon: Network, title: "Network Setup", text: "Router, switch and Wi-Fi setup for your premises.", to: "/services/networking" },
          { icon: Cctv, title: "CCTV Installation", text: "Camera installation, recording setup and testing.", to: "/services/cctv-installation" },
          { icon: Settings, title: "Device Configuration", text: "Configuration of computers, printers and supported technology equipment.", quoteReq: "Installation / Configuration", cta: "REQUEST INSTALLATION" },
        ],
      },
    ],
    cta: { title: "Need Installation or Configuration Help?", text: "Tell us what you need set up — we'll take it from there.", requirement: "Installation / Configuration", waMsg: WA_MESSAGES.general, primaryLabel: "REQUEST INSTALLATION" },
  },
  "/services/networking": {
    path: "/services/networking",
    parent: { label: "Services", to: "/services" },
    metaTitle: "Networking Services in Mallappally | CTS",
    metaDesc:
      "Networking installation and technical support for local businesses and institutions in Mallappally and Pathanamthitta. Request networking service from CTS.",
    eyebrow: "NETWORKING SERVICES",
    h1: "Networking Services",
    intro:
      "A reliable network helps your business stay connected and productive. CTS provides networking installation and technical support for local businesses and institutions.",
    trackEvent: "service_view",
    heroImgKey: "network",
    blocks: [
      { type: "steps", heading: "Our Process", steps: ["Understand", "Plan", "Install", "Configure", "Test"] },
      {
        type: "split",
        eyebrow: "RELIABLE CONNECTIVITY",
        title: "Networks Built for Real Work",
        paragraphs: ["From a single office to multi-room setups, we plan and install networking that matches how your organization actually works."],
        points: ["Network equipment sourced and installed for you", "Structured installation", "Wi-Fi coverage improvements", "Configuration and testing"],
        image: IMAGES.network,
        imgKey: "network",
        alt: "Office networking installation by CTS",
      },
    ],
    form: {
      title: "Request Networking Service",
      sub: "Tell us about your premises and requirement.",
      formType: "service",
      requirement: "Networking",
      fields: ["name", "phone", "organization", "location", "message"],
      customFields: [
        { name: "requirement_type", label: "Requirement Type", type: "select", options: ["New Network Installation", "Wi-Fi Setup / Coverage", "Network Configuration", "Network Troubleshooting", "Network Expansion", "Network Equipment", "Not Sure"] },
        { name: "rooms", label: "Number of rooms (optional)", type: "number" },
        { name: "devices", label: "Approx. users / devices (optional)", type: "number" },
      ],
      submitLabel: "REQUEST NETWORKING SERVICE",
      trackEvent: "service_request_submitted",
    },
    cta: { requirement: "Networking", waMsg: WA_MESSAGES.networking, primaryLabel: "DISCUSS YOUR NETWORK REQUIREMENT" },
  },
  "/services/cctv-installation": {
    path: "/services/cctv-installation",
    parent: { label: "Services", to: "/services" },
    metaTitle: "CCTV Installation in Mallappally & Pathanamthitta | CTS",
    metaDesc:
      "CCTV installation for homes, businesses and institutions in Mallappally. CTS helps you plan, install, configure and test your CCTV system.",
    eyebrow: "CCTV INSTALLATION",
    h1: "CCTV Installation",
    intro:
      "We help customers plan and install CCTV systems suited to their property and monitoring requirements.",
    trackEvent: "service_view",
    heroImgKey: "cctv",
    blocks: [
      { type: "steps", heading: "Our Process", steps: ["Understand", "Plan", "Install", "Configure", "Test"] },
      {
        type: "split",
        eyebrow: "PLANNED, NOT GUESSED",
        title: "Coverage That Matches Your Property",
        paragraphs: ["Camera count, placement and recording setup all depend on your property. We plan the system around what you actually need to see."],
        image: IMAGES.cctv,
        imgKey: "cctv",
        alt: "CCTV camera installed on a building",
      },
    ],
    cta: { requirement: "CCTV", waMsg: WA_MESSAGES.cctv, primaryLabel: "REQUEST A CCTV QUOTE" },
  },
  "/business-solutions/small-business": {
    path: "/business-solutions/small-business",
    parent: { label: "Business Solutions", to: "/business-solutions" },
    metaTitle: "Small Business Technology Solutions | CTS Mallappally",
    metaDesc:
      "Computers, printers, networking, Wi-Fi, CCTV and technical support for small businesses in Mallappally. Plan your business technology setup with CTS.",
    eyebrow: "SMALL BUSINESS",
    h1: "Small Business",
    intro:
      "You shouldn't have to become an IT expert to run your business. CTS can help you choose, install and support the technology your business depends on.",
    trackEvent: "service_view",
    heroImgKey: "smallbiz",
    blocks: [
      {
        type: "cards",
        heading: "What We Can Help With",
        cols: 4,
        items: [
          { icon: Monitor, title: "Computers", text: "Assembled PCs matched to your work and budget." },
          { icon: Printer, title: "Printers", text: "Printers, toner and ink for everyday business printing." },
          { icon: Network, title: "Networking", text: "Network equipment and setup for a dependable business connection." },
          { icon: Wifi, title: "Wi-Fi Coverage & Access Points", text: "Better wireless coverage across your premises." },
          { icon: Cctv, title: "CCTV", text: "CCTV systems planned and installed for your business premises." },
          { icon: Zap, title: "UPS", text: "Standard UPS solutions for computers and essential business equipment." },
          { icon: Wrench, title: "Technical Support", text: "Local on-site support when something goes wrong." },
          { icon: ShieldCheck, title: "Future AMC", text: "Ongoing maintenance plans for your business technology.", badge: "COMING SOON" },
        ],
      },
    ],
    form: {
      title: "Plan Your Business Technology Setup",
      sub: "Tell us about your business and what you need.",
      formType: "business",
      requirement: "Business IT Requirements",
      fields: ["name", "phone", "organization", "location", "message"],
      submitLabel: "PLAN MY BUSINESS TECHNOLOGY SETUP",
      trackEvent: "quote_submitted",
    },
    cta: { requirement: "Business IT Requirements", waMsg: WA_MESSAGES.business, primaryLabel: "DISCUSS YOUR REQUIREMENT" },
  },
  "/business-solutions/offices": {
    path: "/business-solutions/offices",
    parent: { label: "Business Solutions", to: "/business-solutions" },
    metaTitle: "Office Technology Solutions | CTS Mallappally",
    metaDesc:
      "Computers, printers, networking, Wi-Fi, CCTV, installation and technical support for offices in and around Mallappally.",
    eyebrow: "OFFICES",
    h1: "Offices",
    intro:
      "A productive office runs on dependable technology. CTS helps offices with the equipment, connectivity and support they need every day.",
    trackEvent: "service_view",
    heroImgKey: "office",
    blocks: [
      {
        type: "cards",
        heading: "Office Technology, Covered",
        cols: 4,
        items: [
          { icon: Monitor, title: "Computers", text: "Assembled PCs configured around your applications and requirements." },
          { icon: Printer, title: "Printers", text: "Printers and printing consumables matched to your requirements and volume." },
          { icon: Network, title: "Networking", text: "Network equipment, cabling and connectivity for your office." },
          { icon: Wifi, title: "Wi-Fi", text: "Wireless coverage and access points for better office connectivity." },
          { icon: Cctv, title: "CCTV", text: "CCTV planned and installed for office premises." },
          { icon: Download, title: "Installation", text: "Installation, setup and configuration of supported technology equipment." },
          { icon: Wrench, title: "Technical Support", text: "Local on-site support within the CTS service area." },
          { icon: ShieldCheck, title: "Future AMC", text: "Recurring maintenance plans for your office technology.", badge: "COMING SOON" },
        ],
      },
    ],
    cta: { requirement: "Business IT Requirements", waMsg: WA_MESSAGES.business, primaryLabel: "DISCUSS YOUR OFFICE REQUIREMENT" },
  },
  "/business-solutions/hospitals": {
    path: "/business-solutions/hospitals",
    parent: { label: "Business Solutions", to: "/business-solutions" },
    metaTitle: "Hospitals & Clinics Technology Solutions | CTS",
    metaDesc:
      "Workstations, printers, networking, CCTV, UPS and technical support for hospitals and clinics in Mallappally and Pathanamthitta.",
    eyebrow: "HOSPITALS & CLINICS",
    h1: "Hospitals & Clinics",
    intro:
      "CTS provides practical technology solutions for hospitals and clinics, including computers, printers, networking, CCTV, installation and local technical support.",
    trackEvent: "service_view",
    heroImgKey: "hospital",
    blocks: [
      {
        type: "cards",
        heading: "Dependable Technology for Care Environments",
        cols: 4,
        items: [
          { icon: Monitor, title: "Workstations", text: "Computers for reception, billing and administrative work." },
          { icon: Printer, title: "Printers", text: "Printers, toner and ink for everyday documentation." },
          { icon: Network, title: "Networking", text: "Network equipment, cabling and connectivity for offices and work areas." },
          { icon: Cctv, title: "CCTV", text: "Planned and installed CCTV systems for hospital and clinic premises." },
          { icon: Zap, title: "UPS", text: "Standard UPS solutions for computers and essential office equipment." },
          { icon: Download, title: "Installation", text: "Installation, setup and configuration of supported technology equipment." },
          { icon: Wrench, title: "Technical Support", text: "Local on-site support within the CTS service area." },
          { icon: ShieldCheck, title: "Future AMC", text: "Ongoing maintenance plans — register your interest early.", badge: "COMING SOON" },
        ],
      },
    ],
    cta: { requirement: "Business IT Requirements", waMsg: WA_MESSAGES.business, primaryLabel: "DISCUSS YOUR REQUIREMENT" },
  },
  "/business-solutions/banks": {
    path: "/business-solutions/banks",
    parent: { label: "Business Solutions", to: "/business-solutions" },
    metaTitle: "Banks & Financial Offices Technology Solutions | CTS",
    metaDesc:
      "Computers, printers, toners, networking, installation, technical support and CCTV for private-sector banks and financial offices. Contact CTS Mallappally.",
    eyebrow: "BANKS & FINANCIAL OFFICES",
    h1: "Banks & Financial Offices",
    intro:
      "CTS provides technology products, installation and technical support for private-sector banks and financial offices that depend on reliable day-to-day technology.",
    trackEvent: "service_view",
    heroImgKey: "bank",
    blocks: [
      {
        type: "cards",
        heading: "How We Support Financial Offices",
        cols: 4,
        items: [
          { icon: Monitor, title: "Computers", text: "Dependable desktops for counter and office work." },
          { icon: Printer, title: "Printers", text: "Printing solutions for day-to-day documentation." },
          { icon: Package, title: "Toners & Consumables", text: "Printer toners, ink and everyday consumables." },
          { icon: Network, title: "Networking", text: "Network installation and configuration." },
          { icon: Download, title: "Installation", text: "Careful, professional equipment installation." },
          { icon: Wrench, title: "Technical Support", text: "Responsive local technical support." },
          { icon: Cctv, title: "CCTV", text: "CCTV supply and installation for premises." },
        ],
      },
    ],
    cta: { requirement: "Business IT Requirements", waMsg: WA_MESSAGES.business, primaryLabel: "CONTACT CTS" },
  },
};
